/**
 * P0.4 first-time-user onboarding wizard — v2.
 *
 * Rewrite goals (v1 was silently getting users stuck):
 *   - Precondition guards. Every step past step 1 requires a project. If it
 *     doesn't exist we render a recovery card, not a broken form.
 *   - Skip guardrails. Can't skip step 1 unless the user already has a
 *     project. Skipping an infrastructure step from later on used to leave
 *     the wizard permanently broken.
 *   - Back + Restart. Users who wander off can back up or reset without
 *     asking support.
 *   - Durable dismiss. Closing the wizard hides it via localStorage, so it
 *     never re-covers a deep-linked page on later sessions; Restart
 *     un-dismisses deliberately.
 *   - Fewer surprise mutations. useEffect chains no longer fire `advance`
 *     when the current step already matches — cheaper + less log noise.
 *
 * Design constraints (unchanged from v1):
 *   - Additive only. Every action here is reachable from the sidebar.
 *   - Never fail-close a user's primary flow on a wizard hiccup.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  FileSearch,
  FolderPlus,
  KeyRound,
  Notebook,
  Plug,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import type {
  Audit,
  CreateAuditRequest,
  CreateBriefRequest,
  CreateKeywordRunRequest,
  CreateProjectRequest,
  CreateTrackedKeywordRequest,
  Keyword,
  KeywordRun,
  OnboardingStep,
  Project,
  TrackedKeyword,
} from '@seo/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/spinner';
import { cn } from '@/lib/cn';
import { apiGet, apiPost, summarizeApiError } from '@/lib/api';
import { useProjects } from '@/features/projects/use-projects';
import { useCreateProject } from '@/features/projects/use-create-project';
import { useCreateAudit } from '@/features/audits/use-create-audit';
import { useAudits } from '@/features/audits/use-audits';
import { useCreateKeywordRun } from '@/features/keywords/use-create-keyword-run';
import { useKeywordRuns } from '@/features/keywords/use-keyword-runs';
import { useCreateBrief } from '@/features/briefs/use-create-brief';
import {
  useAdvanceOnboarding,
  useBackOnboarding,
  useCompleteOnboarding,
  useOnboardingState,
  useResetOnboarding,
  useSkipOnboarding,
} from '@/features/onboarding/hooks/use-onboarding-state';

/* ------------------------------------------------------------------ */
/* Step metadata                                                       */
/* ------------------------------------------------------------------ */

interface StepMeta {
  step: OnboardingStep;
  index: number;
  label: string;
  icon: typeof FolderPlus;
}

const STEPS_META: StepMeta[] = [
  { step: 'create_project', index: 1, label: 'Add site', icon: FolderPlus },
  { step: 'connect_gsc', index: 2, label: 'Connect GSC', icon: Plug },
  { step: 'first_audit', index: 3, label: 'Run audit', icon: FileSearch },
  { step: 'keyword_research', index: 4, label: 'Keywords', icon: KeyRound },
  { step: 'first_track_and_brief', index: 5, label: 'Track + brief', icon: Notebook },
];

const DISMISS_KEY = 'onboarding-wizard-dismissed';

/* ------------------------------------------------------------------ */
/* Wizard shell                                                        */
/* ------------------------------------------------------------------ */

export function OnboardingWizard(): JSX.Element | null {
  const state = useOnboardingState();
  const complete = useCompleteOnboarding();
  const reset = useResetOnboarding();
  const navigate = useNavigate();
  const step = state.data?.step;

  // Durable dismiss (localStorage): a user who closed the wizard shouldn't
  // have it re-cover whatever page they deep-linked to on every new session.
  // Restarting onboarding (below) un-dismisses, so it stays re-discoverable
  // deliberately rather than by ambush.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    // New state row (e.g., after reset) → un-dismiss.
    if (state.data?.step === 'create_project' && state.data.skipped_steps.length === 0) {
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch {
        /* ignore */
      }
      setDismissed(false);
    }
  }, [state.data?.id, state.data?.step, state.data?.skipped_steps.length]);

  const dismiss = useCallback((): void => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  if (!state.data) return null;
  if (step === 'done') return null;
  if (dismissed) return null;

  const handleDone = async (briefId?: string): Promise<void> => {
    try {
      await complete.mutateAsync();
      toast.success(
        "You're all set. Come back in 7 days to see if your rankings moved.",
      );
      // Hand the user their first artifact instead of closing into thin air.
      if (briefId) {
        await navigate({ to: '/briefs/$briefId', params: { briefId } });
      }
    } catch {
      // Fail-soft; the state will re-hydrate.
    }
  };

  const handleRestart = async (): Promise<void> => {
    try {
      await reset.mutateAsync();
      toast.success('Setup restarted', {
        description: 'Back to the beginning with a clean slate.',
      });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't restart",
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  const hasSkips = (state.data.skipped_steps?.length ?? 0) > 0;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            Get started
          </div>
          <DialogTitle className="text-xl">
            Set up your SEO workspace
          </DialogTitle>
          <DialogDescription>
            Five quick steps to get from empty account to your first ranking
            move.
          </DialogDescription>
        </DialogHeader>

        <StepPips currentStep={step ?? 'create_project'} />

        {/* Consistent min-height stops the dialog from jumping around when
            step content shrinks/grows during transitions. */}
        <div className="min-h-[300px]">
          <WizardBody step={step ?? 'create_project'} onComplete={handleDone} />
        </div>

        {hasSkips ? (
          <div className="flex items-center justify-between border-t border-border/60 pt-3 text-xs text-muted-foreground">
            <span>Wandered off track?</span>
            <button
              type="button"
              onClick={handleRestart}
              disabled={reset.isPending}
              className="inline-flex items-center gap-1 text-foreground/70 underline decoration-dotted underline-offset-2 hover:text-foreground disabled:opacity-50"
            >
              {reset.isPending ? (
                <Spinner className="mr-1 h-3 w-3" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Restart setup
            </button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function StepPips({ currentStep }: { currentStep: OnboardingStep }): JSX.Element {
  const currentIdx = STEPS_META.find((s) => s.step === currentStep)?.index ?? 1;
  return (
    <div className="flex items-center gap-1.5 py-2">
      {STEPS_META.map((meta) => {
        const isDone = meta.index < currentIdx;
        const isCurrent = meta.index === currentIdx;
        return (
          <div key={meta.step} className="flex flex-1 items-center gap-1.5">
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold transition-colors',
                isDone
                  ? 'bg-primary/20 text-primary'
                  : isCurrent
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted text-muted-foreground',
              )}
              aria-current={isCurrent ? 'step' : undefined}
              title={meta.label}
            >
              {isDone ? <CheckCircle2 className="h-3 w-3" /> : meta.index}
            </div>
            {meta.index < STEPS_META.length ? (
              <div
                className={cn(
                  'h-px flex-1 transition-colors',
                  isDone ? 'bg-primary/40' : 'bg-border',
                )}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function WizardBody({
  step,
  onComplete,
}: {
  step: OnboardingStep;
  onComplete: (briefId?: string) => void;
}): JSX.Element {
  switch (step) {
    case 'create_project':
      return <StepCreateProject />;
    case 'connect_gsc':
      return <StepWithProjectGuard><StepConnectGsc /></StepWithProjectGuard>;
    case 'first_audit':
      return <StepWithProjectGuard><StepFirstAudit /></StepWithProjectGuard>;
    case 'keyword_research':
      return <StepWithProjectGuard><StepKeywordResearch /></StepWithProjectGuard>;
    case 'first_track_and_brief':
      return <StepWithProjectGuard><StepTrackAndBrief onComplete={onComplete} /></StepWithProjectGuard>;
    case 'done':
      return <div />;
    default:
      return <div />;
  }
}

/* ------------------------------------------------------------------ */
/* Precondition guard — no project = no downstream step can work       */
/* ------------------------------------------------------------------ */

/**
 * Wrap any step past step 1. If the user has no project we render a
 * recovery card instead of the step form (which would silently fail).
 *
 * Uses `!projects.data` (not `projects.isLoading`) so background refetches
 * don't flash the loading state on every mutation invalidation.
 */
function StepWithProjectGuard({ children }: { children: JSX.Element }): JSX.Element {
  const projects = useProjects();
  const back = useBackOnboarding();

  if (!projects.data) {
    // Initial load only — subsequent refetches keep the previous data visible.
    return (
      <div className="flex min-h-[180px] items-center justify-center rounded-lg border border-border/70 bg-muted/20 text-sm text-muted-foreground">
        <Spinner className="mr-2" /> Loading…
      </div>
    );
  }

  const list = projects.data.items ?? [];
  if (list.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <div className="font-medium text-foreground">
              You need a site first.
            </div>
            <div className="text-muted-foreground">
              Every step from here on attaches to a site. Go back to add one.
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => void back.mutateAsync()}
            disabled={back.isPending}
          >
            {back.isPending ? <Spinner className="mr-1.5" /> : <ArrowLeft className="mr-1 h-3.5 w-3.5" />}
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return children;
}

/* ------------------------------------------------------------------ */
/* Shared footer                                                       */
/* ------------------------------------------------------------------ */

function StepFooter({
  primary,
  currentStep,
  canSkip = true,
  skipLabel = 'Skip for now',
  skipHint,
  showBack = true,
}: {
  primary: JSX.Element;
  currentStep: OnboardingStep;
  canSkip?: boolean;
  skipLabel?: string;
  skipHint?: string;
  showBack?: boolean;
}): JSX.Element {
  const skipMut = useSkipOnboarding();
  const backMut = useBackOnboarding();
  const doSkip = async (): Promise<void> => {
    try {
      await skipMut.mutateAsync(currentStep);
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: "Couldn't skip" });
      toast.error(summary.title, { description: summary.description });
    }
  };
  const doBack = async (): Promise<void> => {
    try { await backMut.mutateAsync(); } catch { /* first step is a no-op */ }
  };
  const showBackButton = showBack && currentStep !== 'create_project';
  return (
    <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-xs text-muted-foreground">{skipHint}</div>
      <div className="flex items-center gap-2">
        {showBackButton ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={doBack}
            disabled={backMut.isPending}
            className="text-muted-foreground"
          >
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Back
          </Button>
        ) : null}
        {canSkip ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={doSkip}
            disabled={skipMut.isPending}
          >
            {skipMut.isPending ? <Spinner className="mr-1.5" /> : null}
            {skipLabel}
          </Button>
        ) : null}
        {primary}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 1 — Create project                                             */
/* ------------------------------------------------------------------ */

const COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'DE', label: 'Germany' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
] as const;

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
] as const;

const DOMAIN_LIKE = /^(https?:\/\/)?([a-z0-9-]+(\.[a-z0-9-]+)+)(\/.*)?$/i;

const ProjectFormSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120, 'Too long'),
  domain: z
    .string()
    .trim()
    .min(1, 'Required')
    .regex(DOMAIN_LIKE, 'Looks like that is not a valid domain'),
  country_code: z.string().length(2),
  language: z.string().regex(/^[a-z]{2,3}$/),
});
type ProjectFormValues = z.infer<typeof ProjectFormSchema>;

function StepCreateProject(): JSX.Element {
  const create = useCreateProject();
  const advance = useAdvanceOnboarding();
  const projects = useProjects();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(ProjectFormSchema),
    defaultValues: { name: '', domain: '', country_code: 'US', language: 'en' },
  });

  const country = watch('country_code');
  const language = watch('language');

  const existing = projects.data?.items ?? [];
  const hasExisting = existing.length > 0;

  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload: CreateProjectRequest = {
        name: values.name.trim(),
        domain: normalizeDomain(values.domain),
        country_code: values.country_code.toUpperCase(),
        language: values.language.toLowerCase(),
      };
      const project = await create.mutateAsync(payload);
      toast.success('Project created', { description: project.name });
      await advance.mutateAsync('connect_gsc');
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not create project',
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  const useExisting = async (): Promise<void> => {
    try {
      await advance.mutateAsync('connect_gsc');
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't continue",
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Add your first site</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Each project represents one website you want to audit, research, and
          create content for.
        </p>
      </div>

      {hasExisting ? (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-emerald-500">
            <CheckCircle2 className="h-4 w-4" />
            You already have {existing.length} project{existing.length === 1 ? '' : 's'}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            You can use one of those and continue, or add another below.
          </div>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-3">
        <TextField
          label="Site name"
          placeholder="Acme Marketing site"
          autoFocus
          error={errors.name?.message}
          {...register('name')}
        />
        <TextField
          label="Domain"
          placeholder="https://example.com"
          hint="Bare hostnames get auto-prefixed with https://."
          error={errors.domain?.message}
          {...register('domain')}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Country</Label>
            <Select
              value={country}
              onValueChange={(v) => setValue('country_code', v, { shouldDirty: true })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.code} — {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Language</Label>
            <Select
              value={language}
              onValueChange={(v) => setValue('language', v, { shouldDirty: true })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.code} — {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="pt-2">
          <StepFooter
            currentStep="create_project"
            // Only allow skipping step 1 if the user already has a project
            // to fall back on. Otherwise skipping leaves the wizard broken.
            canSkip={hasExisting}
            skipLabel="Skip for now"
            skipHint={
              hasExisting
                ? 'Skip and use your existing project.'
                : 'A site is required — every other step needs one.'
            }
            primary={
              <div className="flex items-center gap-2">
                {hasExisting ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={useExisting}
                    disabled={advance.isPending}
                    type="button"
                  >
                    Use existing
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  size="sm"
                  disabled={create.isPending || advance.isPending}
                >
                  {create.isPending ? <Spinner className="mr-1.5" /> : null}
                  Create project
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
            }
          />
        </div>
      </form>
    </div>
  );
}

function normalizeDomain(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/* ------------------------------------------------------------------ */
/* Steps 2–5 — assume project exists (StepWithProjectGuard already ran) */
/* ------------------------------------------------------------------ */

function useFirstProject(): Project | undefined {
  const projects = useProjects();
  return projects.data?.items?.[0];
}

function StepConnectGsc(): JSX.Element {
  const project = useFirstProject()!;
  const advance = useAdvanceOnboarding();
  const [connecting, setConnecting] = useState(false);

  const startConnect = async (): Promise<void> => {
    setConnecting(true);
    try {
      const { url } = await apiGet<{ url: string }>(
        `/v1/projects/${project.id}/integrations/google/authorize`,
      );
      // Optimistic advance — user rarely returns to this step after starting
      // OAuth. If they cancel, they can hit Back or Restart to retry.
      await advance.mutateAsync('first_audit');
      window.location.assign(url);
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't start Google sign-in",
      });
      toast.error(summary.title, { description: summary.description });
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Connect Google Search Console</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Optional. Unlocks real click and impression data in your reports.
          Read-only, disconnect anytime. Skip if you don't have one — reports
          still work with rank + audit data.
        </p>
      </div>

      <StepFooter
        currentStep="connect_gsc"
        primary={
          <Button size="sm" onClick={startConnect} disabled={connecting}>
            {connecting ? <Spinner className="mr-1.5" /> : <Plug className="mr-1 h-3.5 w-3.5" />}
            Connect Google
          </Button>
        }
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 3 — First audit                                                */
/* ------------------------------------------------------------------ */

function StepFirstAudit(): JSX.Element {
  const project = useFirstProject()!;
  const createAudit = useCreateAudit();
  const advance = useAdvanceOnboarding();
  const state = useOnboardingState();
  const [pendingAuditId, setPendingAuditId] = useState<string | null>(null);

  const audits = useAudits();
  const latestForProject = useMemo<Audit | undefined>(
    () => audits.data?.items?.find((a) => a.project_id === project.id),
    [audits.data, project.id],
  );

  const activeAudit = useAuditPoll(pendingAuditId ?? latestForProject?.id);

  // Boolean gate so the effect only re-fires on real transitions. Do NOT put
  // `advance` in deps — react-query's mutation object changes identity on
  // every render (isPending, data, error, etc), which would loop the effect
  // and hammer /v1/onboarding/advance until we hit the 429 rate limit.
  const shouldAdvance =
    activeAudit?.status === 'completed' && state.data?.step === 'first_audit';
  useEffect(() => {
    if (!shouldAdvance) return;
    void advance.mutateAsync('keyword_research').catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAdvance]);

  const failed = activeAudit?.status === 'failed' || activeAudit?.status === 'cancelled';
  const running = !!activeAudit && !failed && activeAudit.status !== 'completed';

  const start = async (): Promise<void> => {
    try {
      const payload: CreateAuditRequest = { project_id: project.id };
      const audit = await createAudit.mutateAsync(payload);
      setPendingAuditId(audit.id);
      toast.success('Audit started', {
        description: 'This runs in the background. Feel free to explore.',
      });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't start audit",
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Run your first audit</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll crawl a sample of your site, check technical SEO signals, and
          give you a health score with a fix list.
        </p>
      </div>

      {running ? (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          <Spinner />
          <div className="min-w-0">
            <div className="font-medium">Audit in progress</div>
            <div className="text-xs text-muted-foreground">
              Currently: {activeAudit?.status ?? 'starting'}. This takes a few
              minutes. Leave this open or come back later.
            </div>
          </div>
        </div>
      ) : failed ? (
        <div className="flex items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <div className="min-w-0">
            <div className="font-medium text-foreground">Last audit didn't finish</div>
            <div className="text-xs text-muted-foreground">
              Status: {activeAudit?.status}. Try starting a new one, or skip
              this step and run an audit later from the Audits page.
            </div>
          </div>
        </div>
      ) : activeAudit?.status === 'completed' ? (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-500">
          <CheckCircle2 className="h-5 w-5" />
          <div>Audit complete — moving on to keywords.</div>
        </div>
      ) : (
        <div className="rounded-lg border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">
          Runs in the background. You'll get a health score, broken-link list,
          and Core Web Vitals for a sample of pages.
        </div>
      )}

      <StepFooter
        currentStep="first_audit"
        primary={
          running || activeAudit?.status === 'completed' ? (
            <Button size="sm" disabled>
              {running ? <Spinner className="mr-1.5" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
              {running ? 'Running…' : 'Complete'}
            </Button>
          ) : (
            <Button size="sm" onClick={start} disabled={createAudit.isPending}>
              {createAudit.isPending ? <Spinner className="mr-1.5" /> : <FileSearch className="mr-1 h-3.5 w-3.5" />}
              {failed ? 'Retry audit' : 'Start audit'}
            </Button>
          )
        }
      />
    </div>
  );
}

function useAuditPoll(id: string | null | undefined) {
  const { data } = useQuery({
    queryKey: ['audit', id],
    queryFn: () => apiGet<Audit>(`/v1/audits/${id!}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (!status) return 5_000;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') return false;
      return 5_000;
    },
  });
  return data;
}

/* ------------------------------------------------------------------ */
/* Step 4 — Keyword research                                           */
/* ------------------------------------------------------------------ */

function StepKeywordResearch(): JSX.Element {
  const project = useFirstProject()!;
  const createRun = useCreateKeywordRun();
  const advance = useAdvanceOnboarding();
  const state = useOnboardingState();
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);

  const runs = useKeywordRuns();
  const latestForProject = useMemo<KeywordRun | undefined>(
    () => runs.data?.items?.find((r) => r.project_id === project.id),
    [runs.data, project.id],
  );

  const activeRun = useKeywordRunPoll(pendingRunId ?? latestForProject?.id);

  // Same boolean-gate pattern as StepFirstAudit — see comment there.
  const shouldAdvance =
    activeRun?.status === 'completed' && state.data?.step === 'keyword_research';
  useEffect(() => {
    if (!shouldAdvance) return;
    void advance.mutateAsync('first_track_and_brief').catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAdvance]);

  const failed = activeRun?.status === 'failed' || activeRun?.status === 'cancelled';
  const running = !!activeRun && !failed && activeRun.status !== 'completed';

  const start = async (): Promise<void> => {
    try {
      const payload: CreateKeywordRunRequest = {
        project_id: project.id,
        seed: { type: 'domain', value: project.domain },
        country_code: project.country_code,
        language: project.language,
        smart_mode: true,
        reuse_audit: true,
      };
      const run = await createRun.mutateAsync(payload);
      setPendingRunId(run.id);
      toast.success('Keyword research started', {
        description: 'Finding terms worth targeting for your site.',
      });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't start keyword research",
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Discover winnable keywords</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll expand seeds from your domain, enrich with volume and
          difficulty, then cluster them so you can see which battles are worth
          fighting.
        </p>
      </div>

      {running ? (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          <Spinner />
          <div className="min-w-0">
            <div className="font-medium">Keyword research in progress</div>
            <div className="text-xs text-muted-foreground">
              Currently: {activeRun?.status ?? 'starting'}.
            </div>
          </div>
        </div>
      ) : failed ? (
        <div className="flex items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <div className="min-w-0">
            <div className="font-medium text-foreground">Last run didn't finish</div>
            <div className="text-xs text-muted-foreground">
              Status: {activeRun?.status}. Try again or skip and continue from
              the Keywords page.
            </div>
          </div>
        </div>
      ) : activeRun?.status === 'completed' ? (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-500">
          <CheckCircle2 className="h-5 w-5" />
          <div>Keywords ready — pick your top one next.</div>
        </div>
      ) : (
        <div className="rounded-lg border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">
          One click seeds from your site's own vocabulary. You can refine later
          from the Keywords page.
        </div>
      )}

      <StepFooter
        currentStep="keyword_research"
        primary={
          running || activeRun?.status === 'completed' ? (
            <Button size="sm" disabled>
              {running ? <Spinner className="mr-1.5" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
              {running ? 'Running…' : 'Complete'}
            </Button>
          ) : (
            <Button size="sm" onClick={start} disabled={createRun.isPending}>
              {createRun.isPending ? <Spinner className="mr-1.5" /> : <KeyRound className="mr-1 h-3.5 w-3.5" />}
              {failed ? 'Retry research' : 'Discover keywords'}
            </Button>
          )
        }
      />
    </div>
  );
}

function useKeywordRunPoll(id: string | null | undefined) {
  const { data } = useQuery({
    queryKey: ['keyword-run', id],
    queryFn: () => apiGet<KeywordRun>(`/v1/keyword-runs/${id!}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (!status) return 5_000;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') return false;
      return 5_000;
    },
  });
  return data;
}

/* ------------------------------------------------------------------ */
/* Step 5 — Track a keyword + generate first brief                     */
/* ------------------------------------------------------------------ */

function StepTrackAndBrief({
  onComplete,
}: {
  onComplete: (briefId?: string) => void;
}): JSX.Element {
  const project = useFirstProject()!;
  const runs = useKeywordRuns();
  const qc = useQueryClient();
  const back = useBackOnboarding();

  const latestRun = useMemo<KeywordRun | undefined>(
    () => runs.data?.items?.find(
      (r) => r.project_id === project.id && r.status === 'completed',
    ),
    [runs.data, project.id],
  );

  const keywords = useQuery({
    queryKey: ['keyword-run-keywords', latestRun?.id, 'onboarding-top'],
    queryFn: async () => {
      const res = await apiGet<{ items: Keyword[] }>(
        `/v1/keyword-runs/${latestRun!.id}/keywords?limit=25`,
      );
      return res.items ?? [];
    },
    enabled: !!latestRun?.id,
  });

  const [selectedKeywordId, setSelectedKeywordId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedKeywordId) return;
    const top = keywords.data?.[0];
    if (top) setSelectedKeywordId(top.id);
  }, [keywords.data, selectedKeywordId]);

  const selectedKeyword = keywords.data?.find((k) => k.id === selectedKeywordId);

  const createBrief = useCreateBrief();
  const complete = useCompleteOnboarding();
  const [submitting, setSubmitting] = useState(false);

  const doTrackAndBrief = async (): Promise<void> => {
    if (!latestRun || !selectedKeyword) return;
    setSubmitting(true);
    try {
      const trackPayload: CreateTrackedKeywordRequest = {
        project_id: project.id,
        keyword: selectedKeyword.term,
        keyword_id: selectedKeyword.id,
        keyword_run_id: latestRun.id,
      };
      try {
        await apiPost<TrackedKeyword>('/v1/tracked-keywords', trackPayload);
      } catch (e) {
        const code = e && typeof e === 'object' && 'code' in e
          ? String((e as { code: unknown }).code) : '';
        if (code !== 'TRACKED_KEYWORD_ALREADY_EXISTS') throw e;
      }
      const briefPayload: CreateBriefRequest = {
        project_id: project.id,
        target_keyword: selectedKeyword.term,
        delivery: 'markdown',
      };
      const createdBrief = await createBrief.mutateAsync(briefPayload);
      toast.success('Tracked + brief started', {
        description: `${selectedKeyword.term} is now on your radar.`,
      });
      await complete.mutateAsync();
      onComplete(createdBrief?.id);
      void qc.invalidateQueries({ queryKey: ['tracked-keywords', project.id] });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't finish setup",
      });
      toast.error(summary.title, { description: summary.description });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">
          Track a keyword and draft your first brief
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll monitor your position weekly and generate a research-backed
          outline your writer can use.
        </p>
      </div>

      {!latestRun ? (
        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div className="space-y-1">
              <div className="font-medium text-foreground">
                No completed keyword research yet.
              </div>
              <div className="text-muted-foreground">
                You skipped step 4 or the run is still processing. Go back to
                run it now, or skip this step and pick a keyword to track
                later from the Keywords page.
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void back.mutateAsync()}
              disabled={back.isPending}
            >
              {back.isPending ? <Spinner className="mr-1.5" /> : <ArrowLeft className="mr-1 h-3.5 w-3.5" />}
              Back to keywords step
            </Button>
          </div>
        </div>
      ) : keywords.isLoading ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 p-3 text-sm">
          <Spinner /> Loading your top keywords…
        </div>
      ) : keywords.data && keywords.data.length > 0 ? (
        <div className="space-y-2">
          <Label className="text-xs">Pick a keyword to track + brief</Label>
          <div className="max-h-60 space-y-1 overflow-y-auto rounded-lg border border-border/70 bg-card/40 p-1.5">
            {keywords.data.slice(0, 10).map((k) => {
              const selected = k.id === selectedKeywordId;
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => setSelectedKeywordId(k.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                    selected
                      ? 'bg-primary/10 text-foreground ring-1 ring-primary/40'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{k.term}</span>
                  {k.volume !== null && k.volume !== undefined ? (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] tabular text-muted-foreground">
                      vol {k.volume}
                    </span>
                  ) : null}
                  {selected ? <ChevronRight className="h-3.5 w-3.5 text-primary" /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">
          No keywords came back from your run. Try a different seed from the
          Keywords page.
        </div>
      )}

      <StepFooter
        currentStep="first_track_and_brief"
        skipLabel="Finish without tracking"
        primary={
          <Button
            size="sm"
            onClick={doTrackAndBrief}
            disabled={submitting || !selectedKeyword || !latestRun}
          >
            {submitting ? <Spinner className="mr-1.5" /> : <Notebook className="mr-1 h-3.5 w-3.5" />}
            Track + generate brief
          </Button>
        }
      />
    </div>
  );
}
